import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Field } from 'react-final-form';
import {
  SimpleSelectField,
  DateField as UIDateField,
  InputField as UIInputField,
} from '@unite-us/app-components';
import { debounce, isEmpty } from 'lodash';
import today from 'common/utils/today';
import featureFlag from 'common/utils/FeatureFlag/FeatureFlag';
import { getInsuranceValuesForValidation, insuranceFieldsValidations } from 'src/components/Insurance/utils';
import { PLAN_TYPES } from 'src/components/Insurance/constants';
import './PaymentsInsuranceFields.scss';

const insurancePlanTypes = Object.values(PLAN_TYPES)
  .filter((plan) => typeof plan === 'string')
  .map((plan) => ({
    display_name: plan.charAt(0).toUpperCase() + plan.slice(1),
    value: plan,
  }));

function getFieldError(meta) {
  return meta.touched ? meta.error : undefined;
}

function CoverageDatesSection({ fieldPrefix, index, form }) {
  const expirationValue = form && form.getFieldState(`${fieldPrefix}.expiration`)?.value;
  const enrollmentValue = form && form.getFieldState(`${fieldPrefix}.enrollment`)?.value;

  return (
    <div className="payments-insurance-date-item">
      <Field name={`${fieldPrefix}.enrollment`}>
        {({ input }) => (
          <UIDateField
            className="payments-insurance-fields__coverage-start"
            id={`insurance-coverage-start-${index}`}
            label="Coverage Start"
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            maxDate={expirationValue ? (Number(expirationValue) - 86400).toString() : null}
          />
        )}
      </Field>

      <Field name={`${fieldPrefix}.expiration`}>
        {({ input }) => (
          <UIDateField
            className="payments-insurance-fields__coverage-end"
            id={`insurance-coverage-end-${index}`}
            label="Coverage End"
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            minDate={enrollmentValue ? (Number(enrollmentValue) + 86400).toString() : today()}
            shouldDisableDate={(date) => enrollmentValue && date.isSame(moment.utc(enrollmentValue), 'day')}
          />
        )}
      </Field>
    </div>
  );
}

CoverageDatesSection.propTypes = {
  fieldPrefix: PropTypes.string.isRequired,
  form: PropTypes.object,
  index: PropTypes.number.isRequired,
};

CoverageDatesSection.defaultProps = {
  form: undefined,
};

export class PaymentsInsuranceFields extends PureComponent {
  constructor(props) {
    super(props);

    this.onChangeType = this.onChangeType.bind(this);
    this.searchInsurancePlanOptions = this.searchInsurancePlanOptions.bind(this);
    this.debouncedSearchInsurancePlanOptions = debounce(this.searchInsurancePlanOptions, 400);
  }

  onChangeType(planType) {
    this.props.onChangeType(planType);
  }

  searchInsurancePlanOptions(search) {
    this.props.searchInsurancePlanOptions(search);
  }

  render() {
    const {
      requiredFields,
      fieldPrefix,
      index,
      insurancePlanOptions,
      initialPlanType,
      initialPlan,
      form,
    } = this.props;

    return (
      <div className="payments-insurance-fields">
        <Field
          name={`${fieldPrefix}.plan_type`}
          validate={getInsuranceValuesForValidation(insuranceFieldsValidations.isRequiredPaymentsPlanType, index)}
        >
          {({ input, meta }) => (
            <SimpleSelectField
              className="payments-insurance-fields__plan-type"
              id={`insurance-plan-type-${index}`}
              label="Plan Type"
              value={input.value}
              onChange={(value) => {
                input.onChange(value);
                this.onChangeType(value);
              }}
              onBlur={input.onBlur}
              options={insurancePlanTypes}
              optionSettings={{ labelKey: 'display_name', valueKey: 'value' }}
              placeholder="Select..."
              disabled={!isEmpty(initialPlanType)}
              required={requiredFields}
              error={getFieldError(meta)}
            />
          )}
        </Field>

        <Field
          name={`${fieldPrefix}.plan`}
          validate={getInsuranceValuesForValidation(insuranceFieldsValidations.isRequiredPaymentsPlan, index)}
        >
          {({ input, meta }) => (
            <SimpleSelectField
              className="payments-insurance-fields__plan"
              id={`insurance-plan-${index}`}
              label="Insurance Plan"
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              onInputChange={this.debouncedSearchInsurancePlanOptions}
              options={insurancePlanOptions.map(({ id, attributes }) => ({
                value: id,
                label: attributes.name,
              }))}
              placeholder="Select..."
              disabled={!isEmpty(initialPlan)}
              required={requiredFields}
              error={getFieldError(meta)}
              searchPlaceholder={insurancePlanOptions.length === 0 ? 'Select Plan Type first' : 'Search'}
            />
          )}
        </Field>

        <div className="payments-insurance-info-item">
          <Field
            name={`${fieldPrefix}.member_id`}
            validate={getInsuranceValuesForValidation(insuranceFieldsValidations.isRequiredPaymentsMemberId, index)}
          >
            {({ input, meta }) => (
              <UIInputField
                className="payments-insurance-fields__member-id"
                id={`insurance-member-id-${index}`}
                label="Member Id"
                value={input.value || ''}
                onChange={(e) => input.onChange(e.target.value)}
                onBlur={input.onBlur}
                required={requiredFields}
                error={getFieldError(meta)}
              />
            )}
          </Field>
        </div>

        <Field name={`${fieldPrefix}.group_id`}>
          {({ input }) => (
            <UIInputField
              className="payments-insurance-fields__group-id"
              id={`insurance-group-id-${index}`}
              label="Insurance Group Id"
              value={input.value || ''}
              onChange={(e) => input.onChange(e.target.value)}
              onBlur={input.onBlur}
            />
          )}
        </Field>

        <CoverageDatesSection
          fieldPrefix={fieldPrefix}
          index={index}
          form={form}
        />
      </div>
    );
  }
}

PaymentsInsuranceFields.propTypes = {
  fieldPrefix: PropTypes.string.isRequired,
  form: PropTypes.object,
  index: PropTypes.number.isRequired,
  insurancePlanOptions: PropTypes.array,
  onChangeType: PropTypes.func.isRequired,
  requiredFields: PropTypes.bool.isRequired,
  searchInsurancePlanOptions: PropTypes.func.isRequired,
  initialPlanType: PropTypes.string,
  initialPlan: PropTypes.string,
};

PaymentsInsuranceFields.defaultProps = {
  insurancePlanOptions: [],
  initialPlanType: '',
  initialPlan: '',
};

export default featureFlag(PaymentsInsuranceFields);
